import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
} from "firebase/auth";
import { auth } from "../firebase.config";
import { useAuthStore } from "../stores";
import { BUILD_TIMESTAMP, getVersionDisplay } from "../version";

const DEV_MODE = process.env.NODE_ENV === "development";

/**
 * Login owns exactly one thing: the shared Firebase email/password session.
 * WHO is at the keyboard is not decided here — that is the global
 * SwitchUserOverlay PIN pad (mounted in App.js), which also covers switching
 * staff mid-session and a reload that restored auth but no staff identity.
 * Keeping a single identity surface is what fixed the old flow, which waited
 * on this page for the users list with timers that read a stale snapshot and
 * so always failed ("Users not loaded"), stranding an already-authenticated
 * user on the login form.
 */
const Login = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const switchingUser = useAuthStore((state) => state.switchingUser);
  const [email, setEmail] = useState("support@bonitabikes.com");
  const [password, setPassword] = useState(DEV_MODE ? "BonitaBikes.69" : "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Format build timestamp to short format: "Mon, Jan 1 -- 3:45 PM"
  const formatBuildTimestamp = (timestamp) => {
    if (!timestamp || timestamp === "2025-10-21") {
      return timestamp;
    }
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return timestamp;

      const dayShort = date.toLocaleDateString("en-US", { weekday: "short" });
      const monthShort = date.toLocaleDateString("en-US", { month: "short" });
      const day = date.getDate();
      const time = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true
      });

      return `${dayShort}, ${monthShort} ${day} -- ${time}`;
    } catch (e) {
      return timestamp;
    }
  };

  // Check storage availability
  useEffect(() => {
    try {
      const testKey = "__storage_test__";
      localStorage.setItem(testKey, "test");
      localStorage.removeItem(testKey);
    } catch (e) {
      console.error("❌ localStorage is NOT available:", e);
      console.warn("⚠️ This may cause auth to not persist!");
      alert(
        "Warning: Browser storage is disabled. Auth may not persist. Please check browser settings."
      );
    }
  }, []);

  // The single exit path. Runs on a fresh sign-in (onAuthStateChanged flips
  // isAuthenticated moments after signInWithEmailAndPassword resolves) and
  // when an already-signed-in session lands on /login: either way this page
  // is done, so go in. With no staff member selected, raise the global PIN
  // pad — the users list it matches against loads inside the app, so nothing
  // here waits on it.
  //
  // Keyboard hand-off. iOS opens the soft keyboard for a focus() only inside
  // a tap — and after a network round trip there is no tap — or when the
  // keyboard is already up and focus moves straight from one input to
  // another. So the keyboard is never allowed to close: the Sign In button
  // doesn't take focus from the password field (its onMouseDown), the Go key
  // submits without blurring, the fields don't go read-only during the
  // spinner, and the pad is raised while this page is still mounted so that
  // PinBoxes can pull focus from the password field in that same commit.
  // Only on the re-run with switchingUser true does this page navigate away.
  // Raising and navigating from one effect run would batch into one commit
  // that removes the password field before the PIN box takes focus; the
  // keyboard then closes, and nothing short of a tap can reopen it.
  //
  // Notification registration deliberately does NOT happen at login. iOS only
  // honors Notification.requestPermission() inside a user gesture, so a
  // timer-driven call is rejected silently -- and once a user has dismissed
  // the prompt the permission is stuck at "denied" and can only be undone in
  // iOS Settings. The bell button on the Today page is the single opt-in.
  useEffect(() => {
    if (!isAuthenticated) return;
    const { selectedUser, startSwitchUser } = useAuthStore.getState();
    if (!selectedUser && !switchingUser) {
      startSwitchUser("login");
      return;
    }
    navigate("/", { replace: true });
  }, [isAuthenticated, switchingUser, navigate]);

  const handleLogin = async () => {
    // Reachable from the keyboard's Go key as well as the button, so a second
    // submit while the first is in flight must be a no-op.
    if (loading) return;
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Note: Persistence is already set in firebase.config.js
      // No need to set it again here to avoid race conditions on mobile
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      console.log("Logged in successfully:", userCredential.user.email);
      // Success: the isAuthenticated effect above navigates. The spinner
      // stays on until this page unmounts, so there is no dead moment where
      // the button looks pressable but the session is mid-handoff.
    } catch (error) {
      console.error("Login error:", error);

      // User-friendly error messages
      switch (error.code) {
        case "auth/invalid-email":
          setError("Invalid email address");
          break;
        case "auth/user-disabled":
          setError("This account has been disabled");
          break;
        case "auth/user-not-found":
          setError("No account found with this email");
          break;
        case "auth/wrong-password":
          setError("Incorrect password");
          break;
        case "auth/invalid-credential":
          setError("Invalid email or password");
          break;
        case "auth/network-request-failed":
          setError("Network error. Please check your connection");
          break;
        default:
          setError(error.message || "Login failed. Please try again");
      }
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.loginCard}>
        <Text style={styles.title}>Welcome to Pix</Text>
        <Text style={styles.subtitle}>Sign in to continue</Text>

        {/* Build Info Banner */}
        <View style={styles.buildInfoBanner}>
          <Text style={styles.buildInfoLabel}>Last Deploy:</Text>
          <Text style={styles.buildInfoValue}>{formatBuildTimestamp(BUILD_TIMESTAMP)}</Text>
          <Text style={styles.buildInfoVersion}>{getVersionDisplay()}</Text>
        </View>

        {error ? (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        ) : null}

        <View style={styles.inputContainer}>
          <Text style={styles.label}>Email</Text>
          <TextInput
            style={styles.input}
            placeholder="Enter your email"
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
          />
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.label}>Password</Text>
          <TextInput
            style={styles.input}
            placeholder="Enter your password"
            value={password}
            onChangeText={setPassword}
            secureTextEntry
            autoCapitalize="none"
            autoCorrect={false}
            enterKeyHint="go"
            onSubmitEditing={handleLogin}
            // Keeps the keyboard up through sign-in (see the hand-off note above).
            blurOnSubmit={false}
          />
        </View>

        <TouchableOpacity dataSet={{ hover: "lift" }}
          style={[styles.loginButton, loading && styles.loginButtonDisabled]}
          onPress={handleLogin}
          // A tap would normally move focus here and close the keyboard. Keep
          // both on the password field — the PIN pad takes them over after
          // sign-in (see the hand-off note above). The press still fires; only
          // the focus change is cancelled.
          onMouseDown={(e) => e.preventDefault()}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.loginButtonText}>Sign In</Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F6F8F7",
    paddingHorizontal: 20,
  },
  loginCard: {
    width: "100%",
    maxWidth: 400,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 30,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 8,
    textAlign: "center",
  },
  subtitle: {
    fontSize: 16,
    color: "#5C6B68",
    marginBottom: 30,
    textAlign: "center",
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 8,
  },
  input: {
    height: 50,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    paddingHorizontal: 15,
    fontSize: 16,
    backgroundColor: "#F4F6F6",
    outlineStyle: "none",
  },
  loginButton: {
    backgroundColor: "#0E7C6B",
    height: 50,
    borderRadius: 6,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 10,
  },
  loginButtonDisabled: {
    backgroundColor: "#E3F2EF",
  },
  loginButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  errorContainer: {
    backgroundColor: "#FBEAE9",
    padding: 12,
    borderRadius: 6,
    marginBottom: 20,
    borderLeftWidth: 4,
    borderLeftColor: "#C4453D",
  },
  errorText: {
    color: "#C4453D",
    fontSize: 14,
  },
  buildInfoBanner: {
    backgroundColor: "#E3F2EF",
    borderRadius: 6,
    padding: 12,
    marginBottom: 20,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
  },
  buildInfoLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#5C6B68",
    marginBottom: 4,
  },
  buildInfoValue: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 2,
  },
  buildInfoVersion: {
    fontSize: 12,
    color: "#8A9794",
  },
});

export default Login;
